/**
 * Shared bullet parser for ReviseRecap summary, Explanation, and Guided Review Step 4.
 *
 * Rules (in order):
 *   1. Split on newline characters first (LF/CRLF, one or more).
 *   2. If no newlines exist in the original text, split on sentence endings
 *      (".", "!", "?") followed by whitespace + a capital letter / digit / quote — BUT
 *      only at positions where the sentence preceding the period contains at least 6 words.
 *      This protects mid-sentence content like "Each angle (A, B, C, and D) …" and
 *      decimals like "3.14".
 *   3. Strip leading bullet markers ("- ", "• ", "* "), trim, drop empties.
 *   4. Drop any bullet shorter than 4 words — short fragments stay attached to the
 *      previous bullet rather than splitting awkwardly.
 *
 * Examples:
 *   toBullets("Step: Locate 6.\nWhy: 6 × 1,00,000 = 6,00,000.")
 *     → ["Step: Locate 6.", "Why: 6 × 1,00,000 = 6,00,000."]
 *
 *   toBullets("HCF = 6. Step 2: 18÷6 = 3, 24÷6 = 4 → 3/4. Check: HCF(3,4) = 1 ✓.")
 *     → keeps "HCF = 6." attached because "HCF = 6" has only 3 words.
 */

const WORD_COUNT_MIN_BEFORE_PERIOD = 6;
const MIN_WORDS_PER_BULLET = 4;

// Keyword markers that always start a new bullet, even when they appear mid-paragraph.
// Matches "Step 1:", "Step 2:", "Example:", "Why:", "Note:", "Tip:", "Remember:" etc.
const KEYWORD_SPLIT = /(?<!^)(?=\b(?:Step\s*\d+|Example|Why|Note|Tip|Remember|Rule|Tip|Key idea|Hint)\s*:)/g;

function countWords(s) {
  if (!s) return 0;
  return s.trim().split(/\s+/).filter(Boolean).length;
}

function splitOnQualifiedSentences(text) {
  // Walk the string; cut at the FIRST ".", "!" or "?" that is:
  //   - followed by whitespace and an opening character ([A-Z0-9"'(“])
  //   - AND the segment from the previous cut to this period has ≥ 6 words.
  const out = [];
  const SENT_END = /[.!?]/;
  const STARTER = /^\s+["“'(A-Z0-9]/;
  let start = 0;
  for (let i = 0; i < text.length; i++) {
    if (!SENT_END.test(text[i])) continue;
    const after = text.slice(i + 1);
    if (!STARTER.test(after)) continue;
    const segment = text.slice(start, i + 1);
    if (countWords(segment) >= WORD_COUNT_MIN_BEFORE_PERIOD) {
      out.push(segment);
      // Skip leading whitespace before the next sentence
      let j = i + 1;
      while (j < text.length && /\s/.test(text[j])) j++;
      start = j;
      i = j - 1;
    }
  }
  if (start < text.length) out.push(text.slice(start));
  return out;
}

export function toBullets(text) {
  if (!text || typeof text !== "string") return [];

  // Pre-split on keyword markers so "Step 1: foo. Step 2: bar." always becomes
  // two bullets, even if no whitespace or newline precedes the marker.
  const preSplit = text.split(KEYWORD_SPLIT).map((s) => s.trim()).filter(Boolean);

  // For each pre-split chunk, apply the standard newline → sentence split.
  const pieces = preSplit.flatMap((chunk) => {
    const hasNewline = /\r?\n/.test(chunk);
    return hasNewline ? chunk.split(/\r?\n+/) : splitOnQualifiedSentences(chunk);
  });

  // Clean up + drop empties
  const cleaned = pieces
    .map((s) => s.trim().replace(/^[-•*]\s*/, ""))
    .filter(Boolean);

  // Coalesce short bullets (<4 words) into the previous bullet to avoid awkward splits.
  const out = [];
  for (const piece of cleaned) {
    if (out.length > 0 && countWords(piece) < MIN_WORDS_PER_BULLET) {
      out[out.length - 1] = out[out.length - 1].replace(/\s*$/, "") + " " + piece;
    } else {
      out.push(piece);
    }
  }
  // If after coalescing the FIRST bullet is still <4 words and there's a second one,
  // merge the first into the second's start.
  if (out.length >= 2 && countWords(out[0]) < MIN_WORDS_PER_BULLET) {
    out[1] = out[0].replace(/\s*$/, "") + " " + out[1];
    out.shift();
  }
  return out;
}
